import React from "react";
import { useTranslation } from "react-i18next";

import { UseCases } from "@planzone/core";
import {
  useEditConstraint,
  useRetrieveConstraint,
} from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { Safeguard } from "@scenes/safeguard";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { Props } from "./types";
import { StartStartForm } from "./startStartForm";
import { EndEndForm } from "./endEndForm";
import { StartEndForm } from "./startEndForm";
import { ContextualMenu } from "@common/contextualMenu";
import { EndStartForm } from "./endStartForm";
import styles from "./styles.scss";

function ConstraintEdition({
  constraint: constraintProp,
  origin: originProp,
  target: targetProp,
  project,
  cancel,
}: Props) {
  const { t } = useTranslation();
  const {
    constraint: retrievedConstraint,
    isRequestPending: isRetrievePending,
    isRequestSuccess: isRetrieveSuccess,
    isRequestFailure: isRetrieveFailure,
    retrieveConstraint,
  } = useRetrieveConstraint(constraintProp.uuid);
  React.useEffect(() => {
    !isRetrievePending &&
      !isRetrieveSuccess &&
      !isRetrieveFailure &&
      retrieveConstraint({ constraintId: constraintProp.uuid });
  }, [constraintProp.uuid]);
  const constraint = retrievedConstraint ?? constraintProp;
  const origin = retrievedConstraint?.origin ?? originProp;
  const target = retrievedConstraint?.target ?? targetProp;
  const [formData, setFormData] = React.useState(constraintProp);
  const [deltaOffset, setDeltaOffset] = React.useState<"after" | "before">(
    "after",
  );
  const contraintType = `${constraint.originDateType}_${constraint.targetDateType}`;
  const { editConstraint, isRequestPending, isRequestFailure } =
    useEditConstraint(constraint);
  const { pushView } = useModal();

  return (
    <Safeguard
      useCases={[UseCases.editConstraint, UseCases.removeConstraint]}
      context={{ project: project || retrievedConstraint?.project }}
    >
      {({ canEditConstraint, canRemoveConstraint }) => (
        <Modal
          header={t(
            `editConstraint.title.${constraint.originDateType}_${constraint.targetDateType}`,
          )}
          close={cancel}
        >
          <div className={styles.constrainElement}>
            {contraintType === "START_START" ? (
              <StartStartForm
                constraint={formData}
                deltaOffset={deltaOffset}
                origin={origin}
                target={target}
                setConstraint={(c) => setFormData({ ...formData, ...c })}
                setDeltaOffset={setDeltaOffset}
              />
            ) : contraintType === "END_END" ? (
              <EndEndForm
                constraint={formData}
                deltaOffset={deltaOffset}
                origin={origin}
                target={target}
                setConstraint={(c) => setFormData({ ...formData, ...c })}
                setDeltaOffset={setDeltaOffset}
              />
            ) : contraintType === "END_START" ? (
              <EndStartForm
                constraint={formData}
                deltaOffset={deltaOffset}
                origin={origin}
                target={target}
                setConstraint={(c) => setFormData({ ...formData, ...c })}
                setDeltaOffset={setDeltaOffset}
              />
            ) : (
              contraintType === "START_END" && (
                <StartEndForm
                  constraint={formData}
                  deltaOffset={deltaOffset}
                  origin={origin}
                  target={target}
                  setConstraint={(c) => setFormData({ ...formData, ...c })}
                  setDeltaOffset={setDeltaOffset}
                />
              )
            )}
            {(canEditConstraint || canRemoveConstraint) && (
              <div className={styles.footer}>
                {canRemoveConstraint && (
                  <Button
                    type="delete"
                    press={() =>
                      pushView({
                        key: UseCases.removeConstraint,
                        data: constraint,
                      })
                    }
                  >
                    {t("editConstraint.remove")}
                  </Button>
                )}
                {canEditConstraint && (
                  <div className={styles.buttons}>
                    <Button type="cancel" press={cancel}>
                      {t("editConstraint.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      status={
                        isRequestPending
                          ? "pending"
                          : isRequestFailure
                            ? "failure"
                            : "none"
                      }
                      isDisabled={isRequestPending}
                      press={() =>
                        editConstraint({
                          constraint,
                          nextConstraint: {
                            ...formData,
                            offsetDays:
                              formData.offsetDays *
                              (deltaOffset === "before" ? -1 : 1),
                          },
                        })
                      }
                    >
                      {t("editConstraint.submit")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </Safeguard>
  );
}

export { ConstraintEdition };
